import printJS from 'print-js';
import { useLoaderDispatch } from 'context/LoaderContext';
import { presignedURLGet } from '../../api/services';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { convertHeicUrlToJpeg } from 'utils/heicUtils';

const isImageExtension = (ext) => {
  switch (ext) {
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'tif':
    case 'tiff':
    case 'webp':
    case 'svg':
    case 'heic':
    case 'heif':
      return true;
    default:
      return false;
  }
};

const isBrowserTextExtension = (ext) => {
  switch (ext) {
    case 'txt':
    case 'csv':
    case 'json':
    case 'xml':
    case 'md':
    case 'markdown':
    case 'log':
    case 'html':
    case 'htm':
      return true;
    default:
      return false;
  }
};

export default function usePrintFile() {
  const setLoader = useLoaderDispatch(true);
  const { addToast } = useToast();

  const getExtension = (file) => {
    const nameForExtension = file?.display_name || file?.file_path || '';
    const lastDotIndex = nameForExtension.lastIndexOf('.');
    return lastDotIndex !== -1 ? nameForExtension.slice(lastDotIndex + 1).toLowerCase() : '';
  };

  const printWithFallback = (printableUrl, type) => {
    try {
      printJS({ printable: printableUrl, type });
    } catch (e) {
      const newWindow = window.open(printableUrl, '_blank');
      if (newWindow) {
        newWindow.onload = function () {
          newWindow.print();
        };
      }
    }
  };

  const pdfTronPrintFile = async (file) => {
    setLoader(true);
    try {
      const extension = getExtension(file);
      if (extension === 'pdf') {
        const response = await presignedURLGet(file.file_path, file.version_id);
        const presignedUrl = response?.data?.file_url;
        if (!presignedUrl) {
          throw new Error('Missing file URL for printing');
        }
        const res = await fetch(presignedUrl);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        try {
          printWithFallback(blobUrl, 'pdf');
        } finally {
          setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
        }
        return;
      }

      if (isImageExtension(extension)) {
        const response = await presignedURLGet(file.file_path, file.version_id);
        const presignedUrl = response?.data?.file_url;
        if (!presignedUrl) {
          throw new Error('Missing file URL for printing');
        }
        if (extension === 'heic' || extension === 'heif') {
          const convertedUrl = await convertHeicUrlToJpeg(presignedUrl);
          try {
            printWithFallback(convertedUrl, 'image');
          } finally {
            setTimeout(() => URL.revokeObjectURL(convertedUrl), 1500);
          }
          return;
        }

        printWithFallback(presignedUrl, 'image');
        return;
      }

      if (isBrowserTextExtension(extension)) {
        const response = await presignedURLGet(file.file_path, file.version_id);
        const presignedUrl = response?.data?.file_url;
        if (!presignedUrl) {
          throw new Error('Missing file URL for printing');
        }

        // Helper to open a new window, write HTML content, and trigger print
        const openAndPrintHtml = (htmlString) => {
          try {
            const printWindow = window.open('', '_blank');
            if (printWindow) {
              printWindow.document.open();
              printWindow.document.write(htmlString);
              printWindow.document.close();
              printWindow.onload = function () {
                printWindow.focus();
                printWindow.print();
              };
            } else {
              // Fallback to printJS raw-html
              printJS({ printable: htmlString, type: 'raw-html' });
            }
          } catch (e) {
            // Final fallback
            printJS({ printable: htmlString, type: 'raw-html' });
          }
        };

        // Escape text for safe insertion inside <pre>
        const escapeHtml = (unsafe) =>
          unsafe.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');

        if (extension === 'html' || extension === 'htm') {
          const res = await fetch(presignedUrl);
          const htmlContent = await res.text();
          openAndPrintHtml(htmlContent);
          return;
        } else {
          const res = await fetch(presignedUrl);
          const textContent = await res.text();
          const htmlWrapper = `<!doctype html><html><head><meta charset="utf-8"><title>Print</title><style>body{margin:16px;font-family:Arial,Helvetica,sans-serif;}pre{white-space:pre-wrap;word-wrap:break-word;}</style></head><body><pre>${escapeHtml(
            textContent
          )}</pre></body></html>`;
          openAndPrintHtml(htmlWrapper);
          return;
        }
      }

      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'File type not supported');
    } catch (error) {
      console.error('Error while printing:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, error?.message || 'Failed to print file');
    } finally {
      setLoader(false);
    }
  };

  return { pdfTronPrintFile };
}
